import { useMemo, useState } from 'react'
import { Download, Printer } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import type {
  ReportMeasure,
  ZeroSellerRow,
} from '@/features/reports/api/use-sales-report-query'
import type { PeriodPreset } from '@/features/reports/lib/period'
import type { ReportFilters } from '@/features/reports/lib/report-filters'
import type { ReportColumn } from '@/features/reports/lib/types'
import type { CustomRange } from '@/features/reports/components/period-switcher'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Skeleton } from '@/components/ui/skeleton'
import { PermissionGate } from '@/shared/components/permission-gate'
import { ACTIONS } from '@/shared/constants/actions'
import { useLocationStore } from '@/shared/store/location-store'
import { PeriodSwitcher } from '@/features/reports/components/period-switcher'
import { ReportFilterBar } from '@/features/reports/components/report-filter-bar'
import { TimeWindowFilter } from '@/features/reports/components/time-window-filter'
import { MeasureSwitcher } from '@/features/reports/components/measure-switcher'
import { CaveatNotes } from '@/features/reports/components/caveat-notes'
import { ScopeLine } from '@/features/reports/components/scope-line'
import { BreakdownTable } from '@/features/reports/views/sales/breakdown-table'
import { BreakdownCards } from '@/features/reports/views/sales/breakdown-cards'
import {
  useSalesReportQuery,
  useZeroSellersQuery,
} from '@/features/reports/api/use-sales-report-query'
import { defaultFilters } from '@/features/reports/lib/report-filters'
import {
  describeRange,
  describeResolvedRange,
  resolvePeriod,
} from '@/features/reports/lib/period'
import { exportCsv } from '@/features/reports/lib/export-csv'
import { formatCurrency, formatDate } from '@/features/reports/lib/format'
import { reportDate } from '@/features/reports/lib/report-zone'

type ArticleTab = 'sold' | 'zero'

/**
 * Articles: what sells, and what does not (CHG-2026-09-manager-reporting).
 *
 * The zero-seller tab is the half the owner asked for that no ranking can show — a list sorted by
 * revenue puts the articles that never sold at the bottom, where nobody scrolls, and an article
 * that has not sold since March looks the same there as one that sold twice yesterday.
 *
 * It needs no inventory data at all: "nie verkauft" is the absence of a sale, and "Tage ohne
 * Verkauf" is the time since the last one. Both come from sales alone, which is why this ships now
 * while the sellout and sell-through report waits for trustworthy stock movements.
 */
export function ArticlesPage() {
  return (
    <PermissionGate action={ACTIONS.REPORT_ARTICLES_VIEW}>
      <ArticlesContent />
    </PermissionGate>
  )
}

function ArticlesContent() {
  const { t } = useTranslation('reports')
  const restaurantIds = useLocationStore((s) => s.restaurantIds)
  const [tab, setTab] = useState<ArticleTab>('sold')
  const [preset, setPreset] = useState<PeriodPreset>('today')
  const [customRange, setCustomRange] = useState<CustomRange | null>(null)
  const [measure, setMeasure] = useState<ReportMeasure>('revenue')
  const [timeWindow, setTimeWindow] = useState({
    daypart: null as ReportFilters['daypart'],
    hourFrom: null as number | null,
    hourTo: null as number | null,
  })

  const now = useMemo(() => new Date(), [])
  const today = reportDate(now)
  const filters = {
    ...defaultFilters(restaurantIds),
    preset,
    customFrom: customRange?.from,
    customTo: customRange?.to,
    customFromTime: customRange?.fromTime,
    customToTime: customRange?.toTime,
    customGrain: customRange?.grain,
    restaurantIds,
    ...timeWindow,
  }
  const resolved = resolvePeriod(preset, now, customRange ?? undefined)

  // What the collapsed filter button says on a phone: the same facts the inline controls show.
  const filterSummary = [
    preset === 'custom' && customRange
      ? describeRange(t, customRange)
      : t(`period.presets.${preset}`),
    timeWindow.daypart
      ? t(`dayparts.${timeWindow.daypart}`)
      : timeWindow.hourFrom != null || timeWindow.hourTo != null
        ? `${timeWindow.hourFrom ?? 0}:00–${timeWindow.hourTo ?? 24}:00`
        : t('timeWindow.allDay'),
  ].join(' · ')

  const sold = useSalesReportQuery({
    ...filters,
    dimension: 'article',
    measure,
    limit: 1000,
  })
  const zero = useZeroSellersQuery(filters, { limit: 500 })

  const handleExport = () => {
    if (tab === 'zero') {
      const rows = zero.data?.rows ?? []
      if (rows.length === 0) return
      exportCsv(
        rows,
        ZERO_SELLER_CSV_COLUMNS(t, today),
        `${t('articles.zeroCsvPrefix')}-${resolved.to.slice(0, 10)}.csv`,
      )
      return
    }
    const rows = sold.data?.rows ?? []
    if (rows.length === 0) return
    exportCsv(
      rows,
      [
        {
          key: 'key',
          label: t('articles.sku'),
          type: 'text',
          accessorFn: (r) => r.key,
        },
        {
          key: 'label',
          label: t('articles.name'),
          type: 'text',
          accessorFn: (r) => r.label ?? '',
        },
        {
          key: 'revenueCents',
          label: t('measure.revenue'),
          type: 'text',
          accessorFn: (r) => r.revenueCents,
        },
        {
          key: 'units',
          label: t('measure.units'),
          type: 'text',
          accessorFn: (r) => r.units,
        },
        {
          key: 'bons',
          label: t('columns.bons'),
          type: 'text',
          accessorFn: (r) => r.bons,
        },
      ],
      `${t('articles.soldCsvPrefix')}-${resolved.to.slice(0, 10)}.csv`,
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('articles.title')}</h1>
          <ScopeLine
            period={
              preset === 'custom' && customRange
                ? describeRange(t, customRange)
                : describeResolvedRange(t, resolved)
            }
          />
        </div>
        <div
          className="hidden flex-wrap items-center gap-2 sm:flex"
          data-print-hide
        >
          <Button variant="outline" size="sm" onClick={() => window.print()}>
            <Printer className="mr-1 h-4 w-4" />
            {t('print')}
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="mr-1 h-4 w-4" />
            {t('exportCsv')}
          </Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3" data-print-hide>
        <Tabs value={tab} onValueChange={(next) => setTab(next as ArticleTab)}>
          <TabsList>
            <TabsTrigger value="sold">
              {t('articles.soldTab', { count: sold.data?.totalRows ?? 0 })}
            </TabsTrigger>
            <TabsTrigger value="zero">
              {t('articles.zeroTab', { count: zero.data?.total ?? 0 })}
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {tab === 'sold' ? (
          <MeasureSwitcher value={measure} onChange={setMeasure} />
        ) : null}
      </div>

      {/* The same filter row as the dashboard and the revenue report — same controls, same order,
          collapsing into one button on a phone. It used to be a bare period control in a row of
          its own, which is how three surfaces built on one endpoint came to look like three
          products. */}
      <ReportFilterBar summary={filterSummary}>
        <PeriodSwitcher
          value={preset}
          custom={customRange}
          onChange={(next, range) => {
            setPreset(next)
            if (range) setCustomRange(range)
          }}
        />
        <TimeWindowFilter value={timeWindow} onChange={setTimeWindow} />
      </ReportFilterBar>

      {tab === 'sold' ? (
        <>
          <div className="hidden lg:block print:block">
            <BreakdownTable
              dimension="article"
              measure={measure}
              rows={sold.data?.rows ?? []}
              total={sold.data?.total ?? EMPTY_TOTALS}
              previous={sold.data?.previous}
              onDrill={null}
              loading={sold.isLoading}
            />
          </div>
          <div className="lg:hidden print:hidden">
            <BreakdownCards
              dimension="article"
              measure={measure}
              rows={sold.data?.rows ?? []}
              total={sold.data?.total ?? EMPTY_TOTALS}
              previous={sold.data?.previous}
              onDrill={null}
              loading={sold.isLoading}
            />
          </div>
          <CaveatNotes caveats={sold.data?.caveats ?? []} />
        </>
      ) : (
        <>
          <p className="text-sm text-muted-foreground">
            {t('articles.zeroExplainer')}
          </p>
          <ZeroSellerTable
            rows={zero.data?.rows ?? []}
            today={today}
            loading={zero.isLoading}
          />
          <CaveatNotes caveats={zero.data?.caveats ?? []} />
        </>
      )}
    </div>
  )
}

function ZeroSellerTable({
  rows,
  today,
  loading,
}: {
  rows: Array<ZeroSellerRow>
  today: string
  loading?: boolean
}) {
  const { t } = useTranslation('reports')
  if (loading) return <Skeleton className="h-72 w-full" />
  if (rows.length === 0) {
    return (
      <div className="rounded-xl border border-border py-16 text-center text-sm text-muted-foreground">
        {t('articles.noZeroSellers')}
      </div>
    )
  }
  return (
    <div
      className="w-full overflow-x-auto rounded-xl border border-border"
      data-print-fit
    >
      <table className="w-full caption-bottom text-sm">
        <thead>
          <tr className="border-b border-border text-muted-foreground">
            <th scope="col" className="h-10 px-3 text-left font-medium">
              {t('articles.name')}
            </th>
            <th scope="col" className="h-10 px-3 text-left font-medium">
              {t('articles.sku')}
            </th>
            <th scope="col" className="h-10 px-3 text-left font-medium">
              {t('articles.group')}
            </th>
            <th scope="col" className="h-10 px-3 text-right font-medium">
              {t('articles.price')}
            </th>
            <th scope="col" className="h-10 px-3 text-left font-medium">
              {t('articles.lastSold')}
            </th>
            <th scope="col" className="h-10 px-3 text-right font-medium">
              {t('articles.daysWithoutSale')}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const days = daysWithoutSale(row.lastSoldAt, today)
            return (
              <tr
                key={row.sku}
                className="border-b border-border last:border-0"
              >
                <td className="px-3 py-2.5">{row.name ?? row.sku}</td>
                <td className="px-3 py-2.5 font-mono text-xs" data-print-tight>
                  {row.sku}
                </td>
                <td className="px-3 py-2.5 text-muted-foreground">
                  {row.categoryLabel ?? '—'}
                </td>
                <td
                  className="px-3 py-2.5 text-right tabular-nums"
                  data-print-tight
                >
                  {row.priceCents != null
                    ? formatCurrency(row.priceCents)
                    : '—'}
                </td>
                <td className="px-3 py-2.5" data-print-tight>
                  {row.lastSoldAt ? (
                    formatDate(row.lastSoldAt)
                  ) : (
                    <span className="text-muted-foreground">
                      {t('articles.neverSold')}
                    </span>
                  )}
                </td>
                <td
                  className="px-3 py-2.5 text-right tabular-nums"
                  data-print-tight
                >
                  {days == null ? '—' : t('articles.days', { count: days })}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/** Whole Vienna days since the last sale; null for "never", which sorts first. */
function daysWithoutSale(
  lastSoldAt: string | null,
  today: string,
): number | null {
  if (!lastSoldAt) return null
  const last = Date.parse(lastSoldAt)
  const now = Date.parse(`${today}T12:00:00Z`)
  if (Number.isNaN(last)) return null
  return Math.max(0, Math.floor((now - last) / 86_400_000))
}

function ZERO_SELLER_CSV_COLUMNS(
  t: (key: string, options?: Record<string, unknown>) => string,
  today: string,
): Array<ReportColumn<ZeroSellerRow>> {
  return [
    {
      key: 'sku',
      label: t('articles.sku'),
      type: 'text',
      accessorFn: (r) => r.sku,
    },
    {
      key: 'name',
      label: t('articles.name'),
      type: 'text',
      accessorFn: (r) => r.name ?? '',
    },
    {
      key: 'group',
      label: t('articles.group'),
      type: 'text',
      accessorFn: (r) => r.categoryLabel ?? '',
    },
    {
      key: 'priceCents',
      label: t('articles.price'),
      type: 'text',
      accessorFn: (r) => r.priceCents ?? '',
    },
    {
      key: 'lastSoldAt',
      label: t('articles.lastSold'),
      type: 'text',
      accessorFn: (r) => r.lastSoldAt ?? '',
    },
    {
      key: 'daysWithoutSale',
      label: t('articles.daysWithoutSale'),
      type: 'text',
      accessorFn: (r) => daysWithoutSale(r.lastSoldAt, today) ?? '',
    },
  ]
}

const EMPTY_TOTALS = { revenueCents: 0, units: 0, bons: 0, skus: 0 }
